import { useMemo, useState } from 'react';

import { AppIcon, ConfirmDelete } from '@backrs_ui/common-ui';

import { AttachedFileChip } from '@backrs_ui/common-ui/src/molecules/Lubav/AttachedFileChip';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { RichTextEditor } from '@backrs_ui/grow-ui/src/components/RichTextEditor/RichTextEditor';
import type { AssigneeNote } from '@backrs_ui/grow-ui/src/redux/coachingDashboard/coachingDashboard.types';

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  IconButton,
  Stack,
  SxProps,
  Theme,
  Tooltip,
  Typography,
} from '@mui/material';

import { buildNotePreview, formatNoteTimestamp } from './coachNotes.utils';

const strings = appStrings.modules.coachingDashboard.coachNotes;

type CoachNoteItemProps = Readonly<{
  note: AssigneeNote;
  expanded: boolean;
  onToggle: (noteId: number) => void;
  onEdit: (note: AssigneeNote) => void;
  onDelete: (note: AssigneeNote) => void;
  isDeleting?: boolean;
}>;

export const CoachNoteItem = ({
  note,
  expanded,
  onToggle,
  onEdit,
  onDelete,
  isDeleting,
}: CoachNoteItemProps) => {
  const preview = buildNotePreview(note.body_plaintext || '');
  const displayDate = formatNoteTimestamp(note.updated_at || note.created_at);
  const fileAttachments = useMemo(
    () => (note.attachments ?? []).filter((a) => !a.is_inline),
    [note.attachments],
  );

  const handleEditClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    onEdit(note);
  };

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const handleDeleteClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    setDeleteDialogOpen(false);
    onDelete(note);
  };

  return (
    <>
      <Accordion
        expanded={expanded}
        onChange={() => onToggle(note.id)}
        disableGutters
        elevation={0}
        sx={accordionSx}
        id={`coach-note-item-${note.id}`}
      >
        <AccordionSummary
          expandIcon={<AppIcon iconName="ChevronDown" diameter="20px" color="Brand/600" />}
          sx={summarySx}
        >
          <Box sx={summaryContentSx}>
            <Box sx={summaryTextSx}>
              <Typography variant="text-sm-semibold" sx={previewTextSx}>
                {preview || strings.emptyPreview}
              </Typography>
              <Typography variant="text-xs-regular" sx={dateTextSx}>
                {displayDate}
              </Typography>
            </Box>
            <Box sx={actionsSx} onClick={(event) => event.stopPropagation()}>
              <Tooltip title={strings.editTooltip} disableInteractive>
                <span>
                  <IconButton
                    id={`coach-note-edit-${note.id}`}
                    aria-label="Edit note"
                    size="small"
                    onClick={handleEditClick}
                    disabled={isDeleting}
                  >
                    <AppIcon iconName="Edit01" diameter="16px" color="Gray/600" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={strings.deleteTooltip} disableInteractive>
                <span>
                  <IconButton
                    id={`coach-note-delete-${note.id}`}
                    aria-label="Delete note"
                    size="small"
                    onClick={handleDeleteClick}
                    disabled={isDeleting}
                  >
                    <AppIcon iconName="Trash01" diameter="16px" color="Gray/600" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={detailsSx}>
          {expanded && (
            <>
              <RichTextEditor readOnly initialValue={note.body} />
              {fileAttachments.length > 0 && (
                <Stack direction="row" sx={attachmentsRowSx} flexWrap="wrap">
                  {fileAttachments.map((attachment) => (
                    <Box
                      key={attachment.id}
                      component="a"
                      id={`coach-note-attachment-${note.id}-${attachment.id}`}
                      href={attachment.uri}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={attachmentLinkSx}
                    >
                      <AttachedFileChip filename={attachment.name} status="done" />
                    </Box>
                  ))}
                </Stack>
              )}
            </>
          )}
        </AccordionDetails>
      </Accordion>

      <ConfirmDelete
        openDialog={deleteDialogOpen}
        setOpenDialog={setDeleteDialogOpen}
        headerText={strings.deleteDialogHeader}
        bodyText={strings.deleteDialogBody}
        handleDelete={handleConfirmDelete}
      />
    </>
  );
};

const accordionSx: SxProps<Theme> = ({ backrs }) => ({
  border: `1px solid ${backrs.colors['Gray/200']}`,
  borderRadius: backrs.radius['radius-md'],
  backgroundColor: backrs.colors['Colors/Base/White'],
  '&:first-of-type': {
    borderRadius: backrs.radius['radius-md'],
  },
  '&:last-of-type': {
    borderRadius: backrs.radius['radius-md'],
  },
  '&:not(:last-of-type)': {
    marginBottom: backrs.spacing['spacing-sm'],
  },
  '&::before': {
    display: 'none',
  },
});

const summarySx: SxProps<Theme> = ({ backrs }) => ({
  paddingX: backrs.spacing['spacing-md'],
  flexDirection: 'row-reverse',
  gap: backrs.spacing['spacing-sm'],
  '& .MuiAccordionSummary-content': {
    margin: `${backrs.spacing['spacing-sm']} 0`,
  },
  '& .MuiAccordionSummary-expandIconWrapper': {
    marginRight: 0,
    transform: 'rotate(-90deg)',
    '&.Mui-expanded': {
      transform: 'rotate(0deg)',
    },
  },
});

const summaryContentSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: backrs.spacing['spacing-md'],
  width: '100%',
});

const summaryTextSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minWidth: 0,
};

const previewTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Gray/900'],
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  maxWidth: { xs: '25ch', md: '30ch' },
});

const dateTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Gray/500'],
});

const actionsSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  gap: backrs.spacing['spacing-xxs'],
});

const detailsSx: SxProps<Theme> = ({ backrs }) => ({
  paddingX: backrs.spacing['spacing-md'],
  paddingBottom: backrs.spacing['spacing-md'],
  borderTop: `1px solid ${backrs.colors['Gray/100']}`,
});

const attachmentsRowSx: SxProps<Theme> = ({ backrs }) => ({
  gap: backrs.spacing['spacing-xs'],
  marginTop: backrs.spacing['spacing-md'],
});

const attachmentLinkSx: SxProps<Theme> = {
  textDecoration: 'none',
  display: 'inline-flex',
};
